import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Typography } from '@mui/material';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';

const VARIANTS = {
  error: { color: 'error.main', Icon: ErrorOutlineRoundedIcon },
  empty: { color: 'success.main', Icon: CheckCircleOutlineRoundedIcon },
  info: { color: 'text.secondary', Icon: CheckCircleOutlineRoundedIcon },
};

/**
 * Shared empty / error state with an explicit recovery affordance.
 */
const StateBlock = ({ variant, title, description, onRetry, retryLabel, 'data-testid': dataTestId }) => {
  const { color, Icon } = VARIANTS[variant] || VARIANTS.info;

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
        py: 6,
        px: 3,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 3,
        backgroundColor: 'grey.50',
      }}
    >
      <Icon sx={{ fontSize: 44, color }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 460 }}>
          {description}
        </Typography>
      )}
      {onRetry && (
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshRoundedIcon />}
          onClick={onRetry}
          sx={{ mt: 1 }}
          data-testid={dataTestId ? `${dataTestId}-retry` : undefined}
        >
          {retryLabel}
        </Button>
      )}
    </Box>
  );
};

StateBlock.propTypes = {
  variant: PropTypes.oneOf(['error', 'empty', 'info']),
  title: PropTypes.string.isRequired,
  description: PropTypes.node,
  onRetry: PropTypes.func,
  retryLabel: PropTypes.string,
  'data-testid': PropTypes.string,
};

StateBlock.defaultProps = {
  variant: 'info',
  description: null,
  onRetry: undefined,
  retryLabel: 'Retry',
  'data-testid': undefined,
};

export default StateBlock;
